import axios from 'axios'
import { toast } from 'react-toastify'
import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import InvitedTable from './components/InvitedTable'
import MembersSearchInput from './components/MembersSearchInput'
import { default as ConfirmationModal } from '../common/ConfirmationModal2'
import TablePlaceholder from './components/TablePlaceholder'

import classes from './InvitedPage.module.scss'
import colors from '../../assets/styles/globals.scss'

import { deserialize } from '../../utility/serialization'
import { debounce } from '../../utility/debounceHelper'
import { requestGetInvitations } from '../../actions/communitiesActions'
import { sendFamilyReminder } from '../../actions/familyActions'
import { deleteInvitation } from '../../actions/invitationActions'


const InvitedPage = () => {
  const dispatch = useDispatch()
  const { loadedInvitedData } = useOutletContext()
  const abortController = useRef(null)
  const communityId = useSelector(state => state.user.current_user.communities[0].id)
  const userId = useSelector(state => state.user.current_user.id)
  const [invitations, setInvitations] = useState(loadedInvitedData?.invitations)
  const [searchText, setSearchText] = useState('')
  const [selectedInvitation, setSelectedInvitation] = useState(null)
  const [selectedInvitationForDeletion, setSelectedInvitationForDeletion] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  const fetchInvited = useCallback(
    ({ search }) =>
      requestGetInvitations({
        communityId,
        search,
        signal: abortController.current?.signal,
      })
        .then(response => {
          const data = deserialize(response.data)
          setInvitations(data.invitations)
        }),
    [communityId]
  )

  const updateInvited = useCallback(
    (params) => {
      setIsLoading(true)
      fetchInvited({ search: searchText, ...params })
        .catch((res) => {
          if (!axios.isCancel(res)) {
            toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT })
          }
        })
        .finally(() => { setIsLoading(false) })
    },
    [fetchInvited, searchText]
  )

  const debouncedSearch = useMemo(
    () => debounce((search) => updateInvited({ search })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const handleSearch = (e) => {
    setSearchText(e.target.value)
    debouncedSearch(e.target.value)
  }

  const handleResendInvitation = () => {
    dispatch(sendFamilyReminder(selectedInvitation, communityId, userId, window.location.origin))
      .then(() => {
        toast.success('Invitation resent successfully', { position: toast.POSITION.TOP_RIGHT })
      })
      .catch(() => {
        toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT })
      })
      .finally(() => {
        setSelectedInvitation(null)
      })
  }

  const handleDeleteInvitation = () => {
    dispatch(deleteInvitation(selectedInvitationForDeletion))
      .then(() => {
        toast.success('Invitation deleted successfully', { position: toast.POSITION.TOP_RIGHT })
        updateInvited({})
      })
      .catch(() => {
        toast.error('Something went wrong. Please try again.', { position: toast.POSITION.TOP_RIGHT })
      })
      .finally(() => {
        setSelectedInvitationForDeletion(null)
      })
  }

  return (
    <main className={classes.wrapper}>
      <header className='py-3 d-flex align-items-center'>
        <div className={classes.searchWrapper}>
          <MembersSearchInput placeholder='Search for Students' value={searchText} onChange={handleSearch} />
        </div>
      </header>
      {invitations?.length === 0 ? (
        <TablePlaceholder message='No Invitations' />
      ) : (
        <InvitedTable
          invitations={invitations}
          isLoading={isLoading}
          onResendInvite={(invitation) => setSelectedInvitation(invitation)}
          onDeleteInvite={(invitation) => setSelectedInvitationForDeletion(invitation)}
        />
      )}
      <ConfirmationModal
        showModal={!!selectedInvitation}
        closeModal={() => setSelectedInvitation(null)}
        icon={
          <i
            className='fa fa-question-circle confirmation-modal-icon'
            aria-hidden='true'
            style={{ color: colors.greyInfoCircle }}
          />
        }
        title='Confirm Resend Invitation'
        message={
          `Are you sure you want to resend the invitation to ${selectedInvitation?.firstName || ''} ${selectedInvitation?.lastName || ''} family?`
        }
        denyMessage='No'
        onDeny={() => setSelectedInvitation(null)}
        proceedMessage='Yes'
        onProceed={handleResendInvitation}
      />
      <ConfirmationModal
        showModal={!!selectedInvitationForDeletion}
        closeModal={() => setSelectedInvitationForDeletion(null)}
        icon={
          <i
            className='fa fa-trash confirmation-modal-icon'
            aria-hidden='true'
            style={{ color: colors.greyInfoCircle }}
          />
        }
        title='Confirm Delete Invitation'
        message={
          `Are you sure you want to delete the ${selectedInvitation?.firstName || ''} ${selectedInvitation?.lastName || ''} family invitation?`
        }
        denyMessage='No'
        onDeny={() => setSelectedInvitationForDeletion(null)}
        proceedMessage='Yes'
        onProceed={handleDeleteInvitation}
      />
    </main>
  )
}

export default InvitedPage
